'use client';
import { FC, useState } from 'react';
import { Flex } from '@osmosis/tailwind-ui/components/Box';
import { print } from '../../../../lib/notes/print';
import { useLearnAccess } from '../../../../context/LearnAccessContext';
import { Button } from '@osmosis/tailwind-ui/components/Button';
import { FormattedMessage } from 'react-intl';
import { Icon } from '@osmosis/tailwind-ui/components/Icon';
import { faPrint } from '@fortawesome/pro-solid-svg-icons/faPrint';
import { ExpiredUserModalType } from '../../../../constants/ExpiredUserModalConfig';
import ExpiredUserLockoutModal from '../../ExpiredUserModal';
import { useUserContext } from '../../../../context/UserContext';
import { ComposerNoteGroup } from '@osmosis/shared/types/notes';
import { textPrimary } from '@osmosis/tailwind-ui/styles/text';

type Props = {
  learnNodeID: number;
  /**
   * Limits the printed document to one group of the node's notes. Omitted when
   * every note the user may see should print.
   */
  group?: ComposerNoteGroup;
};
/**
 * Composer Node Block print button component.
 */
const NotesComposerPrintButton: FC<Props> = ({ learnNodeID, group }) => {
  const [printIsLoading, setPrintIsLoading] = useState(false);
  const [showExpiredModal, setShowExpiredModal] = useState(false);

  const { hasAccess } = useLearnAccess();
  const { user } = useUserContext();

  const handlePrint = async () => {
    setPrintIsLoading(true);
    await print(learnNodeID, group);
    setPrintIsLoading(false);
  };

  const handlePrintClick = () => {
    if (hasAccess) {
      handlePrint();
    } else {
      // Show expired modal for users without access
      setShowExpiredModal(true);
    }
  };

  // Only render if user is logged in
  if (!user) {
    return null;
  }

  return (
    <>
      <Flex cn="items-center">
        <Button
          isLoading={printIsLoading}
          disabled={printIsLoading}
          variant="outline"
          size="s"
          cn={`${textPrimary}`}
          startIcon={<Icon icon={faPrint} />}
          onClick={handlePrintClick}
          data-testid="print-btn"
        >
          <FormattedMessage
            id="learn.notes_section.print.text"
            defaultMessage="Print"
          />
        </Button>
      </Flex>

      {showExpiredModal && (
        <ExpiredUserLockoutModal
          modalType={ExpiredUserModalType.PRINTNOTES}
          isVisible={showExpiredModal}
          hide={() => setShowExpiredModal(false)}
        />
      )}
    </>
  );
};

export default NotesComposerPrintButton;
